import Utils from "@solimat/solimat-web-endpoint/lib/utils";
import { ICnaeResponse } from "@solimat/solimat-web-endpoint/lib/utils/dto";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
// import { getFechaCNAE, watchFechaCNAE } from "./fechaCNAEUtils";

const useGetBlobs = () => {
  const BLOB_BASE_URL = import.meta.env.VITE_REACT_APP_BLOBS_PATH || "";
  const BLOB_KEY = import.meta.env.VITE_REACT_APP_BLOBS_KEY || "";

  const apiBlob = new Utils.UtilsService(BLOB_BASE_URL, BLOB_KEY);
  const downloadingIdsRef = useRef(new Set<string>());
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(
    () => new Set(),
  );
  const mimeToExt: Record<string, string> = {
    "application/pdf": ".pdf",
    "application/msword": ".doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      ".docx",
    "application/vnd.ms-excel": ".xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      ".xlsx",
    "image/jpeg": ".jpg",
    "image/png": ".png",
    "image/gif": ".gif",
    "image/tiff": ".tiff",
    "text/plain": ".txt",
    "application/zip": ".zip",
  };

  const hasValidExtension = (value: string): boolean => {
    const match = value?.split("?")[0].match(/\.([a-zA-Z0-9]{1,5})$/);
    if (!match) return false;

    const ext = match[1];
    return ext.length > 0 && !/^\d+$/.test(ext);
  };

  const resolveFilename = (
    nombre: string,
    uri: string,
    mimeType: string,
  ): string => {
    const safeName = (nombre ?? "archivo").trim();

    // 1) Si el nombre ya tiene extensión real, se conserva
    if (hasValidExtension(safeName)) return safeName;

    // 2) Si la URI trae una extensión real, se usa
    const uriExt = hasValidExtension(uri)
      ? uri.split("?")[0].match(/\.([a-zA-Z0-9]{1,5})$/)?.[1]
      : undefined;
    if (uriExt) return `${safeName}.${uriExt}`;

    // 3) Si el MIME tiene extensión conocida, se usa
    const mimeExt = mimeToExt[mimeType?.split(";")[0].trim() ?? ""];
    if (mimeExt) return `${safeName}${mimeExt}`;

    // 4) Fallback por extensión del nombre o de la URI
    const fallback = getExtension(uri) || getExtension(safeName);
    return fallback ? `${safeName}.${fallback.toLowerCase()}` : safeName;
  };
  const getDocumento = async (sasToken: string) => {
    try {
      // setLoading(true);
      if (sasToken) {
        sasToken = convertirBlobSasUriApimSasUri(sasToken);
        const url = sasToken.startsWith("/") ? sasToken : `/${sasToken}`;

        const response = await apiBlob.getBlob(url);
        // setLoading(false);
        if (response.status === 200) {
          return response.data instanceof Blob
            ? response.data
            : new Blob([response.data]);
        } else {
          console.error(getDocumento, response.statusText);
        }
      }
      return null;
    } catch (err) {
      // setLoading(false);
      console.error(getDocumento, err);
    }
  };

  const handleDownloadBlob = async (
    uri: string,
    nombre: string,
    throwError: boolean = false,
  ) => {
    if (downloadingIdsRef.current.has(uri)) return;

    downloadingIdsRef.current.add(uri);
    setDownloadingIds(new Set(downloadingIdsRef.current));

    const blob = await getDocumento(uri);
    if (blob) {
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      const ext = resolveFilename(nombre, uri, blob.type);
      // const ext = blob.type == 'application/octet-stream';
      link.download = ext;
      document.body.appendChild(link);
      link.click();
      link.remove();
      downloadingIdsRef.current.delete(uri);
      setDownloadingIds(new Set(downloadingIdsRef.current));
      URL.revokeObjectURL(objectUrl);
    } else {
      var nombreArchivo = nombre ? ` "${nombre}"` : ".";
      toast.error(`No se pudo descargar el archivo${nombreArchivo}`);
      downloadingIdsRef.current.delete(uri);
      setDownloadingIds(new Set(downloadingIdsRef.current));
      if (throwError) throw new Error("Error al descargar el archivo");
    }
  };

  const getExtension = (nombre: string): string => {
    const m = nombre?.match(/\.([a-zA-Z0-9]{1,5})$/);
    return m ? m[1].toUpperCase() : "";
  };
  const convertirBlobSasUriApimSasUri = (sasToken: string): string => {
    if (!sasToken) return sasToken;
    if (sasToken.startsWith("http")) {
      const normalizedUrl = sasToken.trim().replace(/\?\?+/g, "?");

      try {
        const url = new URL(normalizedUrl);
        const pathname = url.pathname || "/";
        const fileName = pathname.split("/").filter(Boolean).pop() ?? "";
        const folderPath = pathname.includes("/")
          ? pathname.slice(0, pathname.lastIndexOf("/")) || "/"
          : "/";
        const queryString = url.search ? url.search.slice(1) : "";

        if (!fileName)
          return `${folderPath}${queryString ? `?${queryString}` : ""}`;

        return `${folderPath}?${queryString}${queryString ? "&" : ""}blob=${encodeURIComponent(fileName)}`;
      } catch (error) {
        console.error("convertirBlobSasUriApimSasUri", error);
        return normalizedUrl;
      }
      // const regexHttp = /https?:\/\/(\S+).blob.core.windows.net/g;
      // var testRegexHttp = regexHttp.test(sasToken);
      // var repl = sasToken.replace(regexHttp, "");
      // if (testRegexHttp) sasToken = repl;

      // const regexBlob = /\/(\w+\.\S{3})\?/g;
      // var testRegexBlob = regexBlob.test(sasToken);
      // const match = sasToken.match(regexBlob);
      // var replaceRegexBlob = sasToken.replace(regexBlob, "?");
      // if (testRegexBlob && match && match.length > 0) {
      //   sasToken = `${replaceRegexBlob}&blob=${match[0]}`;
      // }
    }
    return sasToken;
  };

  const isDownloading = (uri: string) => {
    return downloadingIds && downloadingIds.size > 0 && uri && downloadingIds.has(uri);
  };

  return {
    getDocumento,
    handleDownloadBlob,
    getExtension,
    isDownloading,
  };
};

export default useGetBlobs;
